import { IpDevice } from 'app/shared/collections/ip_device';
import { FieldSchemaUnits } from 'app/shared/collections/StatDefinition';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import { Zone } from 'app/shared/collections/zones/zone';
import { ColourObject } from 'app/views/view_types/network/value_getters/associated_ap_value_getter';
import { IMSAggregator } from '../aggregators/aggregator';
import { IMSValueGetter, StatFilterFnGenerator, StatGetter } from './value_getter';

/**
 * This extends the IMSValueGetter to handle a value multiplier.
 * For example, you could multiply the 0-1 connectivity range by 100 to make it a percentage
 */
export class MultiplierValueGetter extends IMSValueGetter {
    _multiplier: number;

    constructor(
        statCollection: ImsTimeCollection = null,
        statKey: string = null,
        units: FieldSchemaUnits = null,
        statFilterFnGenerator: StatFilterFnGenerator = null,
        innerStatGetters: StatGetter[] = [],
        multiplier: number = 1,
    ) {
        //// Public Variables ////
        super(statCollection, statKey, units, statFilterFnGenerator, innerStatGetters);
        this._multiplier = multiplier;
    }

    /**
     * Takes in any IMSObject and returns a value.
     * The default behaviour is to use the statKey
     *
     * NOTE: This is the same as the method it overrides, except it also uses a provided multiplier on the value
     *
     * @param stat An IMSValueGetter
     * @param aggregator
     * @returns a number or string value
     */
    getValueForStat(
        stat,
        aggregator: IMSAggregator = null,
    ): ColourObject | string | number | null | undefined {
        let value = super.getValueForStat(stat, aggregator);

        if (typeof value === 'number') {
            return value * this._multiplier;
        } else return value;
    }

    /**
     * Takes an IPDevice and a cesium time and returns a value
     *
     * @param ipDevice An IPDevice object
     * @param time Cesium JulianDate object
     * @param aggregator
     * @returns a number or string value
     */
    getValueForIPDevice(
        ipDevice: IpDevice,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ColourObject | string | number | null | undefined {
        let value = super.getValueForIPDevice(ipDevice, time, aggregator);

        if (typeof value === 'number') {
            return value * this._multiplier;
        } else return value;
    }

    /**
     * Takes a Zone and a cesium time and returns a value
     * NOTE: This is the same as the method it overrides, except it also uses a provided multiplier on the value
     *
     * @param zone A Zone object
     * @param time Cesium JulianDate object
     * @param aggregator
     * @returns a number or string value
     */
    getValueForZone(zone: Zone, time: Cesium.JulianDate, aggregator: IMSAggregator) {
        let value = super.getValueForZone(zone, time, aggregator);

        if (typeof value === 'number') {
            return value * this._multiplier;
        } else return value;
    }

    /**
     * Gets the multiplier
     * @returns {number} number
     */
    getMultiplier(): number {
        return this._multiplier;
    }
}
